import { useRef, useState } from 'react'
import { AxiosResponse } from 'axios'

import { useMutation } from '@percent/utility'
import { PBPGetResponse } from '@percent/admin-dashboard/services/pbpResponses'
import { BulkDisbursementsResponse } from '@percent/admin-dashboard/api/actions/disbursements/disbursements.types'

export type BulkDisbursementSummary = {
  succeededDisbursementIds: string[]
  failedDisbursementIds: string[]
}

export function useBulkDisbursementAction<TArg>(
  serviceFn: (arg: TArg) => Promise<AxiosResponse<PBPGetResponse<BulkDisbursementsResponse>>>,
  extractIds: (arg: TArg) => string[]
) {
  const [summary, setSummary] = useState<BulkDisbursementSummary | undefined>()
  const lastArgRef = useRef<TArg | undefined>()

  const [{ isPending }, { mutateAsync }] = useMutation(
    serviceFn,
    (response: PBPGetResponse<BulkDisbursementsResponse>) => {
      setSummary({
        succeededDisbursementIds: response.data.succeeded,
        failedDisbursementIds: response.data.failed.map(f => f.disbursementId)
      })
    },
    () => {
      if (lastArgRef.current !== undefined) {
        setSummary({
          succeededDisbursementIds: [],
          failedDisbursementIds: extractIds(lastArgRef.current)
        })
      }
    }
  )

  const execute = async (arg: TArg) => {
    lastArgRef.current = arg
    setSummary(undefined)
    await mutateAsync(arg).catch(() => {})
  }

  const resetSummary = () => setSummary(undefined)

  return { summary, isPending, execute, resetSummary }
}
